<script setup lang="ts">
import { useFormErrors, useFormValues, useValidateForm } from 'vee-validate'
import { computed, inject, Ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSteps } from '/@src/composable/useSteps'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import { ZoppmSchemeTransformer } from '/@src/utils/zoppm'
import { VyzvaSpecificationPublicResponse } from '/@src/client-public'

const { t } = useI18n()
const isReadOnly = inject('isReadOnly')
const formValues = useFormValues()
/** Priznak, ci sa jedna o podanu ziadost. */
const stavPodana = inject<boolean>('stavPodana', false)
const errors = useFormErrors()
const validate = useValidateForm()
const { activeStep, nextStep, setPrevNextStep } = useSteps()
const specResponse = inject<VyzvaSpecificationPublicResponse>('specResponse')

const schemeTransformer = inject<Ref<ZoppmSchemeTransformer>>('schemeTransformer')

const checkListinnePrilohy = (): boolean => {
  let result = false
  specResponse?.zoppmSpec?.povinnePrilohy?.find((priloha) => {
    if (priloha.sposobPredlozenia === 'LISTINNE') {
      result = true
    }
  })
  return result
}

const showUkazovateleProjektu = computed(
  () =>
    schemeTransformer?.value.isSectionEnabled('zoppm.ks.ukazovatele') &&
    formValues.value?.projekt?.ukazovatele?.length > 0
)
// maybe will be needed later
// formValues.value?.projekt?.ukazovatele?.some((ukazovatel) => {
//   const vychodiskovaHodnota = ukazovatel?.vychodiskovaHodnota
//     ? Object.values(ukazovatel.vychodiskovaHodnota).some((value) => value)
//     : false
//   const cielovaHodnota = ukazovatel?.cielovaHodnota
//     ? Object.values(ukazovatel.cielovaHodnota).some((value) => value)
//     : false
//   return vychodiskovaHodnota || cielovaHodnota
// })

const showAktivity = computed(
  () =>
    schemeTransformer?.value.isSectionEnabled('zoppm.ss.aktivity') &&
    formValues?.value?.projekt?.aktivity?.length > 0
)

const showMiestaRealizacie = computed(
  () =>
    schemeTransformer?.value.isSectionEnabled('zoppm.ss.lokality') &&
    formValues?.value?.projekt?.miestaRealizacie?.length > 0
)

const showIdentifikaciaZiadatela = computed(() =>
  schemeTransformer?.value.isSectionEnabled('zoppm.ss.ziadatel-fo')
)

const showPartneriprojektu = computed(() =>
  schemeTransformer?.value.isSectionEnabled('zoppm.ss.partner-fo')
)

const showPrilohyVZhrnuti = computed(
  () =>
    (schemeTransformer?.value.isSectionEnabled('zoppm.ks.prilohy') &&
      formValues.value?.prilohy?.length > 0 &&
      formValues.value?.prilohy?.some(({ priloha }) => priloha?.dokumenty?.length > 0)) ||
    checkListinnePrilohy()
)

const showBodoveKriteria = computed(() =>
  schemeTransformer?.value.isSectionEnabled('zoppm.ks.kriteria')
)

const showPodmienkyPoskytnutiaPrispevku = computed(
  () =>
    schemeTransformer?.value.isSectionEnabled('zoppm.ks.podmienky') &&
    formValues?.value?.podmienkyPoskytnutiaPrispevku?.length > 0
)

const showDalsieUdajeOProjekte = computed(() =>
  schemeTransformer?.value.isSectionEnabled('zoppm.ds.projekt')
)

const showIneFormalneNalezitosti = computed(() =>
  schemeTransformer?.value.isSectionEnabled('zoppm.ds.ziadatel')
)

const showRozpocet = computed(() =>
  schemeTransformer?.value.isSectionEnabled('zoppm.ks.rozpocet')
)

const showDoplnujuceUdajeRozpoctu = computed(
  () =>
    schemeTransformer?.value.isSectionEnabled('zoppm.ds.rozpocet') &&
    formValues?.value?.projekt?.rozpocet?.doplnujuceUdaje?.hodnotyDynoPrvkov &&
    Object.keys(formValues.value.projekt.rozpocet.doplnujuceUdaje.hodnotyDynoPrvkov).some(
      (key) =>
        Object.values(
          formValues.value.projekt.rozpocet.doplnujuceUdaje.hodnotyDynoPrvkov[key]
        ).some((value) => value)
    )
)

const showDalsieUdajeZiadosti = computed(() =>
  schemeTransformer?.value.isSectionEnabled('zoppm.ds.ziadost')
)

const showZdrojeFinancovania = computed(
  () =>
    formValues?.value?.projekt?.financovanie?.statnaPomoc?.schemyStatnejPomoci?.length >
      0 ||
    formValues?.value?.projekt?.financovanie?.ineZdrojeFinancovania?.length > 0 ||
    formValues.value?.projekt?.financovanie?.sposobFinancovania
)

const showCestneVyhlasenie = computed(() => {
  return (
    Array.isArray(formValues?.value.podpisovatelia) &&
    formValues?.value.podpisovatelia.length
  )
})

const showUdajeOProjekte = computed(() =>
  schemeTransformer?.value.isSectionEnabled('zoppm.ss.projekt')
)

const showErrors = computed(
  () => activeStep.value === NazvyKrokov['zhrnutie'] && Object.keys(errors.value)?.length
)

watch(
  () => activeStep.value,
  (curr) => {
    if (curr === NazvyKrokov['zhrnutie']) {
      setTimeout(validate, 500)
    }
  }
)
</script>

<template>
  <SummaryError v-if="showErrors" />
  <ZhrnutieIntro />
  <div v-if="formValues && !isReadOnly">
    <UdajeOProjekte v-if="showUdajeOProjekte" :projekt="formValues.projekt" />
    <DalsieUdajeOProjekte
      v-if="showDalsieUdajeOProjekte"
      :is-valid-section="
        Object.keys(errors).some((podmienka) =>
          podmienka.includes('dalsieUdaje.hodnotyDynoPrvkov.ZOPPM_DS_PROJEKT')
        )
      "
      :dalsie-udaje="formValues.projekt.dalsieUdaje"
    />
    <BodoveKriteria
      v-if="showBodoveKriteria"
      :is-valid-section="
        Object.keys(errors).some((podmienka) => podmienka.includes('bodoveKriteria'))
      "
      :bodove-kriteria="formValues.bodoveKriteria"
    />
    <PodmienkyPoskytnutiaPrispevku
      v-if="showPodmienkyPoskytnutiaPrispevku"
      :is-valid-section="
        Object.keys(errors).some((podmienka) =>
          podmienka.includes('podmienkyPoskytnutiaPrispevku')
        )
      "
      :podmienky-poskytnutia-prispevku="formValues.podmienkyPoskytnutiaPrispevku"
    />
    <UkazovateleProjektu
      v-if="showUkazovateleProjektu"
      :is-valid-section="
        Object.keys(errors).some((podmienka) => podmienka.includes('ukazovatele'))
      "
      :ukazovatele="formValues.projekt.ukazovatele"
    />
    <Aktivity
      v-if="showAktivity"
      :is-valid-section="
        Object.keys(errors).some((podmienka) => podmienka.includes('aktivity'))
      "
      :aktivity="formValues.projekt.aktivity"
    />
    <MiestaRealizacie
      v-if="showMiestaRealizacie"
      :is-valid-section="
        Object.keys(errors).some((podmienka) => podmienka.includes('miestaRealizacie'))
      "
      :miesta-realizacie="formValues.projekt.miestaRealizacie"
    />
    <IdentifikaciaZiadatelaFo v-if="showIdentifikaciaZiadatela" />
    <PartneriProjektuFo v-if="showPartneriprojektu" />

    <IneFormalneNalezitosti
      v-if="showIneFormalneNalezitosti"
      :nalezitosti="formValues.ineFormalneNalezitosti"
    />
    <RozpocetVZhrnuti v-if="showRozpocet" />
    <DoplnujuceUdajeRozpoctu
      v-if="showDoplnujuceUdajeRozpoctu"
      :rozpocet="formValues.projekt.rozpocet.doplnujuceUdaje"
    />
    <ZdrojeFinancovania
      v-if="showZdrojeFinancovania"
      :is-valid-section="
        Object.keys(errors).some((podmienka) => podmienka.includes('financovanie'))
      "
      :financovanie="formValues?.projekt?.financovanie"
    />
    <DalsieUdajeZIadosti
      v-if="showDalsieUdajeZiadosti"
      :udaje="formValues.dalsieUdaje"
      :is-valid-section="
        Object.keys(errors).some((podmienka) =>
          podmienka.includes('dalsieUdaje.hodnotyDynoPrvkov.ZOPPM_DS_ZIADOST')
        )
      "
    />
    <PrilohyVZhrnuti
      v-if="showPrilohyVZhrnuti"
      :prilohy="formValues.prilohy"
      :polozky="formValues.projekt?.rozpocet?.polozky"
      :is-valid-section="
        Object.keys(errors).some((podmienka) => podmienka.includes('priloha'))
      "
    />

    <CestneVyhlasenie
      v-if="showCestneVyhlasenie"
      :podpisovatelia="formValues.podpisovatelia"
    />

    <SummaryError v-if="showErrors" />

    <IDSKButtonGroup v-if="!stavPodana">
      <IDSKButton
        type="submit"
        :disabled="showErrors"
        :data-test-id="`zoppm-zhrnutie-fo-prehlad-next-${nextStep}`"
        @click.prevent="() => setPrevNextStep(1, { ref: undefined })"
      >
        {{ t('dalejNa', { name: nextStep }) }}
      </IDSKButton>

      <IDSKLink href="#" no-visited-state @click="setPrevNextStep(-1)">
        {{ t('spatNaKrok') }}
      </IDSKLink>
    </IDSKButtonGroup>
  </div>
</template>

<i18n lang="yaml">
sk:
  spatNaKrok: 'Späť na predchádzajúci krok'
  dalejNa: 'Ďalej na {name}'
</i18n>
